import type {
  DiscountDto,
  PreviewRequest,
  PriceBreakdownDto,
  PricePreviewDto,
  PromotionDto,
} from '@deli/contracts';
import { useState, type FormEvent } from 'react';
import { useCategories, useProducts } from '../api/catalogue.js';
import { useCustomers } from '../api/customers.js';
import { usePreview } from '../api/pricing.js';
import { ErrorText } from '../components/ErrorText.js';
import { formatMoney } from '../format.js';
import { t } from '../i18n/uk.js';

const tp = t.pricing;

export interface ChosenCustomer {
  readonly id: string;
  readonly fullName: string;
  readonly type: 'b2c' | 'b2b';
  readonly status?: 'pending_approval' | 'active' | 'rejected' | 'blocked';
}

export interface ChosenProduct {
  readonly id: string;
  readonly sku: string;
  readonly name: string;
}

/** "15 %" or "990,00 ₴" — the value as the API returns it (percent or kopecks). */
export const formatDiscount = (mode: 'percent_off' | 'fixed_price', value: number): string =>
  mode === 'percent_off' ? `−${String(value).replace('.', ',')} %` : formatMoney(value);

const date = (iso: string) => iso.split('-').reverse().join('.');

/** "з 01.10.2026 по 31.10.2026", "безстроково". */
export function formatPeriod(validFrom: string | null, validTo: string | null): string {
  if (!validFrom && !validTo) return tp.always;
  return [validFrom && `${tp.from} ${date(validFrom)}`, validTo && `${tp.to} ${date(validTo)}`]
    .filter(Boolean)
    .join(' ');
}

/** "Усі товари", "Категорія: Обличчя / Сироватки", "EXO-1 Сироватка". */
export const targetLabel = (row: DiscountDto | PromotionDto) =>
  row.productId ? (
    <>
      <code>{row.productSku}</code> {row.productName}
    </>
  ) : row.categoryId ? (
    `${tp.targets.category}: ${row.categoryPath ?? '—'}`
  ) : (
    tp.targets.all
  );

export const stateBadge = (state: 'active' | 'scheduled' | 'ended') => (
  <span className={`badge ${state === 'active' ? 'ok' : ''}`}>{tp.states[state]}</span>
);

/** Search by name, phone, email or EDRPOU, then pick one of the first matches. */
export function CustomerPicker({ onPick }: { onPick: (customer: ChosenCustomer) => void }) {
  const [term, setTerm] = useState('');
  const [search, setSearch] = useState<string | null>(null);
  return (
    <div className="picker">
      <div className="inline-form">
        <input
          aria-label={tp.findCustomer}
          placeholder={t.customers.searchPlaceholder}
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              setSearch(term.trim());
            }
          }}
        />
        <button type="button" onClick={() => setSearch(term.trim())}>
          {t.common.search}
        </button>
      </div>
      {search !== null && <CustomerMatches search={search} onPick={onPick} />}
    </div>
  );
}

function CustomerMatches({
  search,
  onPick,
}: {
  search: string;
  onPick: (customer: ChosenCustomer) => void;
}) {
  const found = useCustomers({ search });
  const items = found.data?.items.slice(0, 8) ?? [];
  if (found.isPending) return <p className="muted">{t.common.loading}</p>;
  if (items.length === 0) return <p className="muted">{t.customers.empty}</p>;
  return (
    <ul className="matches">
      {items.map((c) => (
        <li key={c.id}>
          <button type="button" onClick={() => onPick(c)}>
            {c.fullName} <span className="muted">· {t.customers.types[c.type]}</span>
            {c.companyName && <span className="muted"> · {c.companyName}</span>}
          </button>
        </li>
      ))}
    </ul>
  );
}

export function ProductPicker({
  label = tp.findProduct,
  onPick,
}: {
  label?: string;
  onPick: (product: ChosenProduct) => void;
}) {
  const [term, setTerm] = useState('');
  const [search, setSearch] = useState<string | null>(null);
  const find = (e?: FormEvent | { preventDefault(): void }) => {
    e?.preventDefault();
    setSearch(term.trim());
  };
  return (
    <div className="picker">
      <div className="inline-form">
        <input
          aria-label={label}
          placeholder={tp.findProduct}
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && find(e)}
        />
        <button type="button" onClick={() => find()}>
          {t.common.search}
        </button>
      </div>
      {search !== null && (
        <ProductMatches
          search={search}
          onPick={(p) => {
            onPick(p);
            setSearch(null);
            setTerm('');
          }}
        />
      )}
    </div>
  );
}

function ProductMatches({
  search,
  onPick,
}: {
  search: string;
  onPick: (product: ChosenProduct) => void;
}) {
  const found = useProducts({ search, active: true });
  const items = found.data?.items.slice(0, 8) ?? [];
  if (found.isPending) return <p className="muted">{t.common.loading}</p>;
  if (items.length === 0) return <p className="muted">{t.catalogue.empty}</p>;
  return (
    <ul className="matches">
      {items.map((p) => (
        <li key={p.id}>
          <button type="button" onClick={() => onPick(p)}>
            <code>{p.sku}</code> {p.name}{' '}
            <span className="muted">· {formatMoney(p.basePrice)}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

export function CategorySelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (id: string) => void;
}) {
  const categories = useCategories();
  return (
    <label>
      {tp.targets.category}
      <select
        aria-label={tp.targets.category}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{tp.pickCategory}</option>
        {(categories.data ?? []).map((c) => (
          <option key={c.id} value={c.id}>
            {c.path}
          </option>
        ))}
      </select>
    </label>
  );
}

/** One line's way from the base price to the unit price, as the engine reported it. */
export function breakdownText(b: PriceBreakdownDto): string {
  const steps: string[] = [`${tp.preview.base} ${formatMoney(b.base)}`];
  if (b.tier.rule_id) steps.push(`${tp.preview.tier} ${formatMoney(b.tier.result)}`);
  if (b.personal) {
    steps.push(
      `${tp.preview.personal} ${formatDiscount(b.personal.mode, b.personal.value)} = ${formatMoney(b.personal.result)}`,
    );
  }
  if (b.promo) {
    steps.push(
      `${tp.preview.promo} «${b.promo.name}» ${formatDiscount(b.promo.mode, b.promo.value)} = ${formatMoney(b.promo.result)}`,
    );
  }
  if (b.override) {
    const value =
      b.override.mode === 'percent'
        ? formatDiscount('percent_off', b.override.value)
        : formatMoney(b.override.value);
    steps.push(`${tp.preview.override} ${value} = ${formatMoney(b.override.result)}`);
  }
  return steps.join(' → ');
}

export function Breakdown({ breakdown }: { breakdown: PriceBreakdownDto }) {
  return <div className="breakdown muted">{breakdownText(breakdown)}</div>;
}

export function PricedTable({ priced }: { priced: PricePreviewDto }) {
  return (
    <table className="table">
      <thead>
        <tr>
          <th>{t.catalogue.sku}</th>
          <th>{t.catalogue.name}</th>
          <th className="num">{tp.preview.qty}</th>
          <th className="num">{tp.preview.base}</th>
          <th className="num">{tp.preview.unit}</th>
          <th className="num">{tp.preview.lineTotal}</th>
        </tr>
      </thead>
      <tbody>
        {priced.lines.map((line, i) => (
          <tr key={`${line.sku}-${i}`}>
            <td>
              <code>{line.sku}</code>
            </td>
            <td>
              {line.name}
              <Breakdown breakdown={line.breakdown} />
            </td>
            <td className="num">{line.qty}</td>
            <td className="num">{formatMoney(line.breakdown.base)}</td>
            <td className="num">{formatMoney(line.unitPrice)}</td>
            <td className="num">{formatMoney(line.lineTotal)}</td>
          </tr>
        ))}
        <tr>
          <th colSpan={5}>{tp.preview.subtotal}</th>
          <th className="num">{formatMoney(priced.subtotal)}</th>
        </tr>
      </tbody>
    </table>
  );
}

interface Line extends ChosenProduct {
  qty: number;
}

/**
 * A sample cart priced by the server (PRC-11 preview). With `customer` fixed (a discount being
 * edited) the buyer can't change; `draft` carries the form's unsaved values, or `invalid` when the
 * form can't be sent yet.
 */
export function PricePreview({
  customer: fixed,
  draft,
  invalid = false,
}: {
  customer?: ChosenCustomer | null;
  draft?: PreviewRequest['draft'];
  invalid?: boolean;
}) {
  const preview = usePreview();
  const [chosen, setChosen] = useState<ChosenCustomer | null>(null);
  const [guestType, setGuestType] = useState<'b2c' | 'b2b'>('b2c');
  const [lines, setLines] = useState<Line[]>([]);
  const customer = fixed === undefined ? chosen : fixed;

  const calculate = () =>
    preview.mutate({
      customerId: customer?.id ?? null,
      customerType: customer ? undefined : guestType,
      lines: lines.map((l) => ({ sku: l.sku, qty: l.qty })),
      draft,
    });

  return (
    <div className="preview">
      <h2>{tp.preview.title}</h2>
      <p className="muted hint">{draft ? tp.preview.draftHint : tp.preview.hint}</p>
      {fixed === undefined && (
        <div className="inline-form">
          <span>{tp.preview.priceFor}:</span>
          {chosen ? (
            <>
              <strong>{chosen.fullName}</strong>
              <span className="muted">{t.customers.types[chosen.type]}</span>
              <button type="button" onClick={() => setChosen(null)}>
                {tp.preview.guest}
              </button>
            </>
          ) : (
            <label>
              {tp.preview.guestType}
              <select
                aria-label={tp.preview.guestType}
                value={guestType}
                onChange={(e) => setGuestType(e.target.value as 'b2c' | 'b2b')}
              >
                <option value="b2c">{t.customers.types.b2c}</option>
                <option value="b2b">{t.customers.types.b2b}</option>
              </select>
            </label>
          )}
        </div>
      )}
      {fixed === undefined && !chosen && <CustomerPicker onPick={setChosen} />}
      <ProductPicker
        label={tp.preview.addProduct}
        onPick={(p) => setLines([...lines, { id: p.id, sku: p.sku, name: p.name, qty: 1 }])}
      />
      {lines.length === 0 ? (
        <p className="muted">{tp.preview.empty}</p>
      ) : (
        <ul className="cart">
          {lines.map((line, i) => (
            <li key={`${line.id}-${i}`}>
              <code>{line.sku}</code> {line.name}
              <input
                type="number"
                min={1}
                max={10000}
                aria-label={`${tp.preview.qty}: ${line.sku}`}
                value={line.qty}
                onChange={(e) =>
                  setLines(
                    lines.map((l, j) =>
                      j === i ? { ...l, qty: Math.max(1, Number(e.target.value) || 1) } : l,
                    ),
                  )
                }
              />
              <button
                type="button"
                className="icon"
                aria-label={`${t.common.remove}: ${line.sku}`}
                onClick={() => setLines(lines.filter((_, j) => j !== i))}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="actions">
        <button
          type="button"
          disabled={lines.length === 0 || invalid || preview.isPending}
          onClick={calculate}
        >
          {tp.preview.calculate}
        </button>
        {invalid && <span className="muted">{tp.preview.fixDraft}</span>}
      </div>
      <ErrorText error={preview.error} />
      {preview.data && <PricedTable priced={preview.data} />}
    </div>
  );
}
